<template>
  <div class="notes-bar">
    <ul class="nav nav-tabs px-3" id="assemblyTabs" role="tablist">
      <li class="nav-item" role="presentation">
        <button
          class="nav-link active"
          id="home-tab"
          data-bs-toggle="tab"
          data-bs-target="#summary"
          type="button"
          role="tab"
          aria-controls="summary"
          aria-selected="true"
        >
          {{ $t('BoardsAssemblyBar.summary') }}
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
          class="nav-link"
          id="profile-tab"
          data-bs-toggle="tab"
          data-bs-target="#parts"
          type="button"
          role="tab"
          aria-controls="parts"
          aria-selected="false"
        >
          {{ $t('BoardsAssemblyBar.parts') }}
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
          class="nav-link"
          id="contact-tab"
          data-bs-toggle="tab"
          data-bs-target="#guide"
          type="button"
          role="tab"
          aria-controls="guide"
          aria-selected="false"
        >
          {{ $t('BoardsAssemblyBar.guide') }}
        </button>
      </li>
    </ul>

    <div v-if="this.loading" class="pt-3 text-center loader">
      <BeatLoader v-if="this.loading" />
    </div>
    <div
      v-else-if="this.error"
      class="text-center mt-3"
    >
      {{ $t('BoardsAssemblyBar.loadingError') }}
    </div>

    <div v-else class="tab-content py-3" id="assembly-bar-tabs">
      <div class="tab-pane fade show active" id="summary" role="tabpanel">
        <div class="row g-3 px-3 m-0">
          <template v-if="this.assemblySummary.sides.length > 0">
            <div class="col-6">
              {{ $t('BoardsAssemblyBar.sides') }}
            </div>
            <div v-if="this.assemblySummary.sides.length === 1" class="col-6 text-end">
              {{ $t('BoardsAssemblyBar.singleSided', { side: this.assemblySummary.sides[0] }) }}
            </div>
            <div v-else class="col-6 text-end">
              {{ $t('BoardsAssemblyBar.doubleSided') }}
            </div>
          </template>

          <div class="col-6">
            {{ $t('BoardsAssemblyBar.distinctComponents') }}
          </div>
          <div class="col-6 text-end">
            {{ this.assemblySummary.totalComponents }}
          </div>

          <div class="col-6">
            {{ $t('BoardsAssemblyBar.smtComponents') }}
          </div>
          <div class="col-6 text-end">
            {{ this.assemblySummary.smtCount }}
          </div>

          <div class="col-6">
            {{ $t('BoardsAssemblyBar.thtComponents') }}
          </div>
          <div class="col-6 text-end">
            {{ this.assemblySummary.thtCount }}
          </div>

          <div class="col-6">
            {{ $t('BoardsAssemblyBar.pressFitComponents') }}
          </div>
          <div class="col-6 text-end">
            {{ this.assemblySummary.pressFitCount }}
          </div>
        </div>

        <hr>

        <div v-if="this.notes.length === 0" class="row text-center px-3">
          <i class="fas fa-3x fa-check-circle text-success pb-3" />
          <div>{{ $t('BoardsAssemblyBar.pcbReady') }}</div>
        </div>

        <p v-else class="px-3">
          {{ $t('BoardsAssemblyBar.analyzeNotice') }}
        </p>

        <NotesAccordion
          :notes="notes"
          @note-click="drawNote"
        />
      </div>

      <div class="tab-pane fade" id="parts" role="tabpanel">
        <div class="row g-3 m-0">
          <div class="col-12">
            <div class="input-group">
              <span class="input-group-text"><i class="fa fa-search" /></span>
              <input type="search" v-model="externalQuery" class="form-control">
            </div>
          </div>

          <div class="cell small-12">
            <VueGoodTable
              class="part-table"
              :columns="partTableColumns"
              :rows="parts"
              :search-options="{
                enabled: true,
                externalQuery: externalQuery,
                searchFn: searchFn,
              }"
              :select-options="{
                enabled: true,
                selectOnCheckboxOnly: true,
                disableSelectInfo: true,
              }"
              @selected-rows-change="partSelectionChanged"
              style-class="vgt-table bordered"
            >
              <template #emptystate>
                {{ $t('BoardsAssemblyBar.noPartsNotice') }}
              </template>
              <template #table-row="props">
                <template v-if="props.column.field === 'place'">
                  <i class="far fa-check-square" v-if="props.row.place" />
                  <i class="far fa-square" v-else />
                  <span v-if="props.row.mountType">&nbsp;{{ $t(`BoardsAssemblyBar.${props.row.mountType}`) }}</span>
                </template>
                <span v-else-if="props.column.field === 'value'">
                  {{ props.row.matchDescription || props.row.mpn || props.row.value }}
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </VueGoodTable>
          </div>
        </div>
      </div>

      <div class="tab-pane px-3" id="guide" role="tabpanel">
        <Guide :parts="guideParts" :project-token="projectToken" />
      </div>
    </div>
  </div>
</template>

<script>
import messages from 'locales/BoardsAssemblyBar';
import { getRequest } from 'helper/request';

import { VueGoodTable } from 'vue-good-table-next';
import Guide from 'components/boards/Guide.vue';
import BeatLoader from 'components/shared/BeatLoader.vue';
import NotesAccordion from './NotesAccordion.vue';

export default {
  name: 'AssemblyBar',
  props: {
    metaPath: String,
    projectToken: String,
  },
  components: {
    NotesAccordion,
    BeatLoader,
    Guide,
    VueGoodTable,
  },
  data() {
    return {
      loading: false,
      notes: [],
      assemblySummary: {
        sides: [],
        totalComponents: null,
        smtCount: null,
        thtCount: null,
      },
      parts: [],
      guideParts: [],
      bus: this.$root.bus,
      externalQuery: '',
      partTableColumns: [
        {
          field: 'name',
          label: this.$t('BoardsAssemblyBar.name'),
        },
        {
          field: 'value',
          label: this.$t('BoardsAssemblyBar.value'),
        },
        {
          field: 'place',
          label: this.$t('BoardsAssemblyBar.place'),
          html: true,
          globalSearchDisabled: true,
        },
        {
          field: 'side',
          label: this.$t('BoardsAssemblyBar.side'),
          formatFn: (val) => this.$t(`BoardsAssemblyBar.${val}`),
          globalSearchDisabled: true,
        },
      ],
      error: null,
    };
  },
  methods: {
    loadMeta() {
      getRequest(this.metaPath).then((data) => {
        this.notes = data.notes.map((note) => {
          note.selected = false;
          return note;
        });
        this.assemblySummary = data.assemblySummary;
        this.parts = data.parts;
        this.guideParts = data.guide;
        this.bus.emit('loadParts', this.parts);
        this.bus.emit('updatePartPlacement', this.parts.filter((part) => part.place));
      }).catch((e) => {
        this.loading = false;
        this.error = e;
      }).finally(() => {
        this.loading = false;
      });
    },
    drawNote(note) {
      if (note.shapes.length > 0) {
        note.selected = !note.selected;
        const method = note.selected ? 'addNote' : 'removeNote';
        this.bus.emit(method, note);
      } else if (note.identifiers.length > 0) {
        const parts = this.parts.filter((part) => note.identifiers.includes(part.name));
        if (parts.length > 0) {
          this.bus.emit('updatePartFocus', { parts, updateCamera: true });
        }
      }
    },
    initBus() {
      const self = this;
      self.bus.on('partClicked', (id) => {
        self.parts.forEach((part) => {
          if (part.id === id) {
            self.externalQuery = part.name;
            self.bus.emit('updatePartFocus', { parts: [part], updateCamera: false });
          }
        });
      });
    },
    partSelectionChanged(evt) {
      this.bus.emit('updatePartFocus', { parts: evt.selectedRows });
    },
    searchFn(row, col, cellValue, searchTerm) {
      if (searchTerm.length === 0) {
        return true;
      }
      if (cellValue === null) {
        return false;
      }

      const fullTextSearchColumns = ['value'];
      if (fullTextSearchColumns.includes(col.field)) {
        return cellValue.toLowerCase().includes(searchTerm.trim().toLowerCase());
      }

      return searchTerm.trim().toLowerCase() === cellValue.toLowerCase();
    },
  },
  mounted() {
    this.loading = true;
    const self = this;
    this.bus.on('viewerReady', () => {
      self.bus.emit('loadParts', self.parts);
      self.bus.emit('updatePartPlacement', self.parts.filter((part) => part.place));
    });
    this.loadMeta();
    this.initBus();
  },
  updated() {
  },
  unmounted() {
    this.tabs.destroy();
    this.accordion.destroy();
  },

  i18n: { messages },
};
</script>

<style scoped lang="scss">
.part-table {
  .vgt-table {
    margin-bottom: 0;
    border-radius: 0;
  }
}
</style>
